import { useEffect, useState, useMemo, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Check, Trophy, AlertTriangle } from "lucide-react";
import { api, apiErr } from "../lib/api";
import { Page, Loader, Empty, GoldButton, GhostButton, Card } from "../components/UI";
import { pointsInfo } from "../components/Arena";

const ERR_MSG = {
  PLAYER_MATCH_COUPON_LIMIT_REACHED: "You have reached your maximum of 3 coupons for this match.",
  TEAM_MATCH_COUPON_LIMIT_REACHED: "Your team already has all 11 coupons submitted.",
  EVENT_ALREADY_STARTED: "One of your events has already started.",
  EVENT_NOT_ELIGIBLE: "One of your events is no longer eligible.",
  EVENT_BEYOND_HORIZON: "One of your events starts beyond the match horizon.",
  COUPON_SELECTION_COUNT_INVALID: "A coupon must contain 1 to 6 selections.",
  MATCH_NOT_OPEN: "The submission window is not open.",
  PLAYER_NOT_IN_TEAM: "You are not on a roster for this match.",
};

export default function CouponBuilder() {
  const { matchId } = useParams();
  const nav = useNavigate();
  const [match, setMatch] = useState(null);
  const [events, setEvents] = useState(null);
  const [picks, setPicks] = useState([]); // {event_id, market_id, selection_id, odds, event_label, market_name, label}
  const [usage, setUsage] = useState({ used: 0, max: 3 });
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [result, setResult] = useState(null);

  const load = useCallback(() => {
    api.get(`/matches/${matchId}`).then(({ data }) => setMatch(data)).catch(() => setMatch(false));
    api.get(`/matches/${matchId}/eligible-events`).then(({ data }) => setEvents(data.events)).catch(() => setEvents([]));
    api.get(`/matches/${matchId}/my-coupons`).then(({ data }) => setUsage({ used: data.used, max: data.max })).catch(() => {});
  }, [matchId]);

  useEffect(() => { load(); }, [load]);

  const total = useMemo(() => {
    if (picks.length === 0) return 0;
    const t = picks.reduce((acc, p) => acc * parseFloat(p.odds), 1);
    return Math.round(t * 100) / 100;
  }, [picks]);
  const info = pointsInfo(total);

  const toggle = (ev, m, s) => {
    setPicks((prev) => {
      const exists = prev.find((p) => p.selection_id === s.selection_id);
      if (exists) return prev.filter((p) => p.selection_id !== s.selection_id);
      if (prev.length >= 6) { toast.error("Maximum 6 selections per coupon"); return prev; }
      return [...prev, {
        event_id: ev.id, market_id: m.market_id, selection_id: s.selection_id,
        odds: s.odds, event_label: `${ev.home_name} vs ${ev.away_name}`,
        market_name: m.name, label: s.label,
      }];
    });
  };

  const submit = async () => {
    setBusy(true);
    try {
      const token = (window.crypto?.randomUUID?.() || String(Date.now()));
      const { data } = await api.post(`/matches/${matchId}/coupons`, {
        selections: picks.map((p) => ({ event_id: p.event_id, market_id: p.market_id, selection_id: p.selection_id })),
        client_token: token,
      });
      setResult(data);
      toast.success(`Coupon accepted — position #${data.team_sequence_number}`);
    } catch (e) {
      const d = e?.response?.data?.detail;
      const code = typeof d === "object" ? d.code : d;
      toast.error(ERR_MSG[code] || apiErr(e));
      setConfirm(false);
    } finally {
      setBusy(false);
    }
  };

  if (match === false) return <Page><Empty label="Match not found" /></Page>;
  if (!match || !events) return <Loader />;

  const open = ["SUBMISSION_OPEN", "SUBMISSION_IN_PROGRESS"].includes(match.status);
  const mySide = match; // informational

  if (result) {
    return (
      <Page className="max-w-xl">
        <div data-testid="coupon-accepted" className="rounded-2xl foil-border bg-gradient-to-b from-[#161616] to-[#070707] p-8 text-center">
          <Check className="w-14 h-14 text-emerald-400 mx-auto" />
          <h1 className="font-heading text-3xl font-black uppercase tracking-tight text-slate-50 mt-3">Coupon Submitted</h1>
          <div className="mt-4 grid grid-cols-2 gap-3 text-left">
            <Stat label="Team position" value={`#${result.team_sequence_number} OF 11`} />
            <Stat label="My usage" value={`${result.player_sequence}/3`} />
            <Stat label="Accepted total odds" value={result.total_odds} />
            <Stat label="Potential team points" value={`+${result.potential_team_points}`} />
          </div>
          <p className="mt-5 font-mono text-sm text-amber-300 uppercase tracking-wide" data-testid="waiting-opponent">
            Waiting for opponent coupon #{result.team_sequence_number}
          </p>
          <div className="flex gap-3 justify-center mt-6">
            <Link to={`/match/${match.id}`}><GhostButton>View match</GhostButton></Link>
            {usage.used + 1 < usage.max && (
              <GoldButton onClick={() => { setResult(null); setPicks([]); setConfirm(false); load(); }}>Build another</GoldButton>
            )}
          </div>
        </div>
      </Page>
    );
  }

  return (
    <Page>
      <Link to={`/match/${match.id}`} className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-[#E5B842] mb-4" style={{ transition: "color 0.15s ease" }}>
        <ArrowLeft className="w-4 h-4" /> Back to match
      </Link>
      <h1 className="font-heading text-4xl font-black uppercase tracking-tight text-slate-50">ArenaBook Coupon Builder</h1>
      <p className="text-slate-400 text-sm mt-1">{match.home_club?.name} vs {match.away_club?.name} · pick 1–6 selections</p>

      {!open && <Card className="p-4 mt-4 text-amber-300 text-sm flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Submission window is {match.status.replace(/_/g, " ")}.</Card>}

      <div className="grid lg:grid-cols-3 gap-6 mt-6">
        <div className="lg:col-span-2 space-y-4" data-testid="events-list">
          {events.length === 0 ? <Empty label="No eligible events right now" /> : events.map((ev) => (
            <Card key={ev.id} className="p-4">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <div className="font-heading font-bold uppercase tracking-tight text-slate-50">{ev.home_name} vs {ev.away_name}</div>
                  <div className="text-[11px] font-mono text-slate-500">{ev.sport} · starts {new Date(ev.event_start_time).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</div>
                </div>
              </div>
              {ev.markets.map((m) => (
                <div key={m.market_id} className="mb-2">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono mb-1">{m.name}</div>
                  <div className="flex flex-wrap gap-2">
                    {m.selections.map((s) => {
                      const sel = picks.some((p) => p.selection_id === s.selection_id);
                      return (
                        <button
                          key={s.selection_id}
                          data-testid={`selection-${s.selection_id}`}
                          onClick={() => toggle(ev, m, s)}
                          className={`px-3 py-1.5 rounded-lg text-sm font-semibold border flex items-center gap-2 ${sel ? "bg-[#E5B842] text-[#000000] border-[#E5B842]" : "bg-[#000000] text-slate-200 border-[#2b2512] hover:border-[#E5B842]/40"}`}
                          style={{ transition: "border-color 0.15s ease" }}
                        >
                          {s.label} <span className="font-mono">{s.odds}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </Card>
          ))}
        </div>

        <div>
          <Card className="p-5 sticky top-24" data-testid="coupon-slip">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading text-lg font-bold uppercase text-slate-50 flex items-center gap-2"><Trophy className="w-5 h-5 text-[#E5B842]" /> My Coupon</h3>
              <span className="font-mono text-xs text-slate-400">{usage.used}/{usage.max} used</span>
            </div>
            {picks.length === 0 ? (
              <p className="text-slate-500 text-sm">Select 1–6 outcomes to build your coupon.</p>
            ) : (
              <ul className="space-y-2 mb-3" data-testid="coupon-picks">
                {picks.map((p) => (
                  <li key={p.selection_id} className="flex justify-between gap-2 text-sm">
                    <span className="text-slate-300 truncate">{p.event_label} · {p.label}</span>
                    <span className="font-mono text-[#E5B842]">{p.odds}</span>
                  </li>
                ))}
              </ul>
            )}
            <div className="border-t border-[#2b2512] pt-3 flex items-center justify-between">
              <span className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">Total odds</span>
              <span data-testid="total-odds" className="font-heading text-2xl font-black text-[#E5B842] tabular-nums">{total.toFixed(2)}</span>
            </div>
            {info && (
              <div data-testid="potential-points" className={`mt-2 rounded-lg border px-3 py-2 text-xs font-semibold ${info.tone}`}>
                POTENTIAL TEAM POINTS: +{info.pts} · {info.label.replace(/^\+\d · /, "")}
              </div>
            )}

            {!confirm ? (
              <GoldButton className="w-full mt-4" data-testid="review-coupon-btn" disabled={picks.length === 0 || !open} onClick={() => setConfirm(true)}>
                Review & submit
              </GoldButton>
            ) : (
              <div className="mt-4 space-y-2" data-testid="confirm-panel">
                <p className="text-xs text-slate-400 font-mono uppercase">Confirm — odds locked at acceptance, server-authoritative.</p>
                <GoldButton className="w-full" data-testid="confirm-submit-btn" disabled={busy} onClick={submit}>
                  {busy ? "Submitting…" : "Confirm submission"}
                </GoldButton>
                <GhostButton className="w-full" onClick={() => setConfirm(false)}>Back</GhostButton>
              </div>
            )}
          </Card>
        </div>
      </div>
    </Page>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-lg bg-[#0f0f0f] border border-[#2b2512] p-3">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">{label}</div>
      <div className="font-heading text-xl font-black text-slate-50 mt-0.5">{value}</div>
    </div>
  );
}
